import React from 'react';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import MHeader from '@/components/ui/organisms/MHeader';
import { MButton } from '@/components/ui/atoms/MButtons';
import { IconAlertSquareRoundedFilled, IconSquareRoundedCheckFilled, IconLoader, IconHistory, IconClipboardText, IconMedicalCross, IconBubbleText, IconFolders, IconPinned, IconAlertTriangle } from '@tabler/icons-react';
import MDocumentUploadOptions from '@/components/ui/molecules/MDocumentUploadOptions';
import { MDocUploadModal } from '@/components/ui/molecules/MDocUploadModal';
import { getMemberInfo } from '@/hooks/useMembers';
import { useDocument, buildDocumentChipFilters } from '@/hooks/useDocument';
import { useFamilyStats } from '@/hooks/useFamilyStats';
import { setNavigationSource, clearNavigationSource } from '@/utils/navigationSource';
import styles from './document.module.css';
import MPill from '@/components/ui/atoms/MPill';
import { FileCategory, DocumentStatus } from '@/types/document.dto';
import { scheduleUploadProgressCleanup, checkAndCleanupIfExpired } from '@/utils/upload-progress-storage';
import DocumentDrawer from './DocumentDrawer';
import DocumentCommentDrawer from './DocumentCommentDrawer';
import MFab from '@/components/ui/atoms/MFab';
import MButtonCard from '@/components/ui/atoms/MButtonCard';
import MDialog from '@/components/ui/atoms/MDialog';
import { MTourTrigger } from '@/components/ui/organisms/MTourTrigger';
import { MHelpIcon } from '@/components/ui/atoms/MHelpIcon';

import MLoader from '@/components/ui/atoms/MLoader';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const DocumentsLanding: React.FC = () => {
  // Clear stored years when landing page loads
  React.useEffect(() => {
    localStorage.removeItem('documentListingAvailableYears');
  }, []);

  const { showToast } = useToast ? useToast() : { showToast: () => {} }; // fallback for provider
  const tourTriggerRef = React.useRef<any>(null);
  const dispatch = useAppDispatch();
  const loggedInMember = useSelector((state: RootState) => state.auth?.member);

  // Clear navigationSource when documents landing loads
  React.useEffect(() => {
    clearNavigationSource();
  }, []);

  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || '';
  const isSelf = !memberInfo.onBehalf;
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;

  // Get family stats for health summary availability check
  const { memberStats, fetchMemberStats } = useFamilyStats({ memberInfo });

  // Fetch member stats on mount
  React.useEffect(() => {
    if (memberId) {
      fetchMemberStats(memberId);
    }
  }, [memberId, fetchMemberStats]);

  // Reset to logged-in user on documents landing mount
  // COMMENTED OUT: Preserve selected member when navigating to documents landing
  // React.useEffect(() => {
  //   if (loggedInMember) {
  //     dispatch(clearSelectedMember());
  //   }
  // }, []);

  // Restore navigation and document hooks
  const navigate = useNavigate();
  const { fetchDocuments, downloadDocument, updateDocumentFlag, deleteDocument, updateDocumentNotes, vectorSearchDocuments, getDocumentTypeCounts } = useDocument(memberId);
  
  // Separate hook for logged-in user's processing and uncategorized data (for banners only)
  const loggedInMemberId = loggedInMember?.id || '';
  const { fetchProcessingDocuments: fetchLoggedInProcessing, getDocumentTypeCounts: getLoggedInTypeCounts } = useDocument(loggedInMemberId);

  function handleNavigateWithCategory(category: FileCategory) {
    localStorage.removeItem('documentFilters');
    navigate(`/documents/list?category=${category}`);
  }

  // Modal open state for upload modal
  const [uploadModalOpen, setUploadModalOpen] = React.useState(false);
  // Accept uploading count from UploadReviewPage via navigation state
  const [showUploadOptions, setShowUploadOptions] = React.useState(false);
  const uploadOptionsRef = React.useRef(null);
  const [totalCount, setTotalCount] = React.useState(0);
  // Single-select chip logic, but keep the same filter structure for backend compatibility
  const [chipFilters, setChipFilters] = React.useState({
    isImportant: false,
    isPrivate: false,
    availableOffline: false,
    category: null as number | null,
  });

  // Helper to set only one filter at a time
  const handleChipSelect = (chip: 'Recent' | 'Offline' | 'Favorites' | 'Private') => {
    if (chip === 'Recent') {
      setChipFilters({ isImportant: false, isPrivate: false, availableOffline: false, category: null });
    } else if (chip === 'Offline') {
      setChipFilters({ isImportant: false, isPrivate: false, availableOffline: true, category: null });
    } else if (chip === 'Favorites') {
      setChipFilters({ isImportant: true, isPrivate: false, availableOffline: false, category: null });
    } else if (chip === 'Private') {
      setChipFilters({ isImportant: false, isPrivate: true, availableOffline: false, category: null });
    }
  };
  const [commentLoading, setCommentLoading] = React.useState(false);

  const [pillCounts, setPillCounts] = React.useState({
    prescriptions: 0,
    labReports: 0,
    dischargeSummaries: 0,
    medicalReceipts: 0,
    insurances: 0,
    uncategorized: 0,
    other: 0,
    identity: 0,
    healthMemories: 0
  });
  // Separate state for logged-in member's uncategorized count (for banner)
  const [loggedInUncategorized, setLoggedInUncategorized] = React.useState(0);
  const [processingCounts, setProcessingCounts] = React.useState<{ pending: number; total: number }>({ pending: 0, total: 0 });
  const [shouldStopPolling, setShouldStopPolling] = React.useState(false);
  const initialProcessingTotal = React.useRef<number>(0);
  const processingPollingRef = React.useRef<NodeJS.Timeout | null>(null);
  const [allDocuments, setAllDocuments] = React.useState<any[]>([]);
  const [documents, setDocuments] = React.useState<any[]>([]);
  const [originalDocuments, setOriginalDocuments] = React.useState<any[]>([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debounceTimer, setDebounceTimer] = React.useState<NodeJS.Timeout | null>(null);
    const [pageLoading, setPageLoading] = React.useState(true);
const hasLoadedOnce = React.useRef(false);
  // Drawer mode: 'none' | 'document' | 'comment'
  const [drawerMode, setDrawerMode] = React.useState<'none' | 'document' | 'comment' | 'note'>('none');
  const [selectedDoc, setSelectedDoc] = React.useState<any | null>(null);
  // Emergency Card info drawer state
  const [showEmergencyCardInfo, setShowEmergencyCardInfo] = React.useState(false);
  const [showHealthSummaryInfo, setShowHealthSummaryInfo] = React.useState(false);

  const handleUnlockEmergencyCard = () => {
    setShowEmergencyCardInfo(false);
    navigate('/profile/emergency-card/activate', { state: { memberId: memberInfo.id, backUrl: window.location.pathname } });
  };

  const handleAddDocumentsFromHealthSummary = () => {
    setShowHealthSummaryInfo(false);
    setShowUploadOptions(true);
  };

  type DocumentTypeCountsLanding = {
    healthMemories?: number;
    uncategorized?: number;
    totalDocuments?: number;
  };

  // Fetch processing (Pending + Processed) documents for landing banner
  React.useEffect(() => {
    let cancelled = false;

    async function fetchProcessingSummary() {
      try {
        // Check if cleanup time has expired and clear localStorage if needed
        checkAndCleanupIfExpired();
        
        const res: any = await fetchLoggedInProcessing({});
        const list: any[] = Array.isArray(res?.data) ? res.data : [];
        const pending = list.length;
        
        if (!cancelled) {
          // If new files arrive, cancel any scheduled cleanup and update counts
          if (pending > 0) {
            if (initialProcessingTotal.current === 0) {
              // First time seeing processing files
              initialProcessingTotal.current = pending;
            } else if (pending > initialProcessingTotal.current) {
              // New files added during processing, update total
              initialProcessingTotal.current = pending;
            }
          }
          
          // Use stored initial total, or current pending if no initial total stored
          const total = initialProcessingTotal.current > 0 ? initialProcessingTotal.current : pending;
          setProcessingCounts({ pending, total });
          
          // When all processing is complete, schedule cleanup after 1 minute
          if (pending === 0 && initialProcessingTotal.current > 0) {
            // Schedule cleanup of upload progress localStorage after 1 minute
            scheduleUploadProgressCleanup();
          }
        }
      } catch {
        // ignore; processing banner is optional
      }
    }

    fetchProcessingSummary();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInMemberId]);

  // Split documents for processing section (all except processed)
const processingDocuments = allDocuments.filter(doc => doc.status !== DocumentStatus.Processed);

  // Derive an increasing "current uploading" index for banner text (1 of N, 2 of N, ...)
  const totalProcessingFiles = processingCounts.total;
  const processedSoFar = totalProcessingFiles - processingCounts.pending;
  const currentUploadingIndex = totalProcessingFiles > 0
    ? Math.min(processedSoFar + 1, totalProcessingFiles)
    : 0;

  // Poll processing endpoint while there are pending documents to keep banner in sync
  React.useEffect(() => {
    if (processingCounts.total > 0 && !shouldStopPolling) {
      if (!processingPollingRef.current) {
        processingPollingRef.current = setInterval(async () => {
          try {
            const res: any = await fetchLoggedInProcessing({});
            const list: any[] = Array.isArray(res?.data) ? res.data : [];
            const pending = list.length;
            
            // Check if cleanup time has expired
            const cleanupExpired = checkAndCleanupIfExpired();
            
            // If cleanup expired and no pending files, hide banner
            if (cleanupExpired && pending === 0) {
              initialProcessingTotal.current = 0;
              setProcessingCounts({ pending: 0, total: 0 });
              return;
            }
            
            // If new files arrive, cancel scheduled cleanup and update counts
            if (pending > 0) {
              if (pending > initialProcessingTotal.current) {
                // New files added, update total
                initialProcessingTotal.current = pending;
              }
              // Reset polling stop flag when new files arrive
              setShouldStopPolling(false);
            }
            
            // When all processing is complete, schedule cleanup after 1 minute and stop polling
            if (pending === 0 && initialProcessingTotal.current > 0) {
              // Schedule cleanup of upload progress localStorage after 1 minute
              scheduleUploadProgressCleanup();
              // Stop polling but keep banner visible
              setShouldStopPolling(true);
            }
            
            // Always update counts for banner display
            const total = initialProcessingTotal.current > 0 ? initialProcessingTotal.current : pending;
            setProcessingCounts({ pending, total });

            // While processing is ongoing, also refresh landing counts
            // Fetch logged-in member's uncategorized count for banner
            try {
              const loggedInRaw = await getLoggedInTypeCounts({ mode: 'landing' });
              const loggedInCounts = loggedInRaw as DocumentTypeCountsLanding;
              setLoggedInUncategorized(loggedInCounts?.uncategorized ?? 0);
            } catch {
              // best-effort; ignore errors here
            }
            
            // Fetch selected member's counts for pills
            try {
              const raw = await getDocumentTypeCounts({ mode: 'landing' });
              const counts = raw as DocumentTypeCountsLanding;
              setPillCounts(prev => ({
                ...prev,
                healthMemories: counts?.healthMemories ?? 0,
                uncategorized: counts?.uncategorized ?? 0,
                totalDocuments: counts?.totalDocuments ?? 0,
              }));
              // Update totalCount from API response
              setTotalCount(counts?.totalDocuments ?? 0);
            } catch {
              // best-effort; ignore errors here
            }
          } catch {
            // best-effort only; ignore errors
          }
        }, 2000);
      }
    } else if (processingPollingRef.current) {
      clearInterval(processingPollingRef.current);
      processingPollingRef.current = null;
    }

    return () => {
      if (processingPollingRef.current) {
        clearInterval(processingPollingRef.current);
        processingPollingRef.current = null;
      }
    };
  }, [processingCounts.total, loggedInMemberId, fetchLoggedInProcessing, getLoggedInTypeCounts]);

// Main list: only ocrReadable: true and status === Processed, always use filtered documents
const processedDocuments = documents.filter(doc => doc.status === DocumentStatus.Processed);

  // Fetch all documents and update state (no document-type-counts call here)
  const fetchAllDocuments = async () => {
    try {
      // Fetch all documents for polling and processing logic (no ocrReadable filter)
      const docs = await fetchDocuments({ filters: {}, sort: [] }) as { data: any[] };
      const list = Array.isArray(docs.data) ? docs.data : [];
      setAllDocuments(list);
      setDocuments(list);
      // Note: totalCount is now set from pillCounts.totalDocuments in the landing counts API
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      // no-op for now
    }
  };

  // Initial page load: show loader and fetch documents
  React.useEffect(() => {
    localStorage.removeItem("documentFilters");
    let cancelled = false;
    async function sequenceFetches() {
      if (!hasLoadedOnce.current) {
        setPageLoading(true);
        try {
          // Fetch all documents once on initial load
          const docs = await fetchDocuments({ filters: {}, sort: [] }) as { data: any[] };
          const list = Array.isArray(docs.data) ? docs.data : [];
          if (!cancelled) {
            setAllDocuments(list);
            setDocuments(list);
          }
        } catch (err) {
          // Optionally handle error
        } finally {
          if (!cancelled) {
            setPageLoading(false);
            hasLoadedOnce.current = true;
          }
        }
      }
    }
    sequenceFetches();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Fetch logged-in member's uncategorized count for banner
  React.useEffect(() => {
    let cancelled = false;

    async function fetchLoggedInUncategorized() {
      try {
        const raw = await getLoggedInTypeCounts({ mode: 'landing' });
        const counts = raw as DocumentTypeCountsLanding;
        if (!cancelled) {
          setLoggedInUncategorized(counts?.uncategorized ?? 0);
        }
      } catch {
        // ignore, banner is optional
      }
    }

    fetchLoggedInUncategorized();
    return () => {
      cancelled = true;
    };
    // Run when logged-in member changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInMemberId]);

  // Fetch selected member's counts for landing (document-type-counts)
  React.useEffect(() => {
    let cancelled = false;

    async function fetchHealthMemoriesCount() {
      try {
        // Use selected member's counts (not logged-in member)
        const raw = await getDocumentTypeCounts({ mode: 'landing' });
        const counts = raw as DocumentTypeCountsLanding;
        if (!cancelled) {
          setPillCounts(prev => ({
            ...prev,
            healthMemories: counts?.healthMemories ?? 0,
            uncategorized: counts?.uncategorized ?? 0,
            totalDocuments: counts?.totalDocuments ?? 0
          }));
          // Update totalCount from API response
          setTotalCount(counts?.totalDocuments ?? 0);
        }
      } catch {
        // ignore, landing can still render without this
      }
    }

    fetchHealthMemoriesCount();
    return () => {
      cancelled = true;
    };
    // Run when memberId changes (selected member)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Track GTM user_data_loaded event on page load with total document count
  React.useEffect(() => {
    if (!memberInfo?.id || pageLoading) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
      screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_UPLOADS,
      additionalUserProperties: {
        total_document_count: String(totalCount),
      },
    });
  }, [memberInfo?.id, pageLoading, totalCount]);

  // Track processed document ids to detect transitions
  const processedDocIdsRef = React.useRef<Set<string>>(new Set());
  const initialProcessedLoaded = React.useRef(false);

  // On first load, set processedDocIdsRef so no toast fires for already-processed docs
  React.useEffect(() => {
    if (!initialProcessedLoaded.current && !pageLoading) {
      const initialProcessed = allDocuments.filter(doc => doc.status === DocumentStatus.Processed);
      processedDocIdsRef.current = new Set(initialProcessed.map(doc => doc._id || doc.id));
      initialProcessedLoaded.current = true;
    }
  }, [allDocuments, pageLoading]);

  // Show toast when all processing documents are done (processingDocuments.length transitions from >0 to 0)
  const prevProcessingCount = React.useRef(processingDocuments.length);
  React.useEffect(() => {
    if (prevProcessingCount.current > 0 && processingDocuments.length === 0) {
      //showToast({ message: 'All documents have been successfully processed.', variant: 'success' });
      window.location.reload();
    }
    prevProcessingCount.current = processingDocuments.length;
  }, [processingDocuments.length]);

  // Poll silently every 2s after initial load
  React.useEffect(() => {
    if (!pageLoading && processingDocuments.length > 0 && initialProcessedLoaded.current) {
      const interval = setInterval(fetchAllDocuments, 2000);
      return () => clearInterval(interval);
    }
  }, [pageLoading, processingDocuments.length, memberId]);

  // Fetch all documents when memberId changes
  React.useEffect(() => {
    fetchAllDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Cache original documents on first fetch
  React.useEffect(() => {
    if (allDocuments.length && !originalDocuments.length) {
      setOriginalDocuments(allDocuments);
    }
  }, [allDocuments, originalDocuments.length]);

  // On filter/search change, only update documents
  React.useEffect(() => {
    async function fetchFilteredDocuments() {
            try {
        const filters = buildDocumentChipFilters(chipFilters);
        const docs = await fetchDocuments({ filters, sort: [] }) as { data: any[] };
        setDocuments(Array.isArray(docs.data) ? docs.data : []);
      } catch (err) {
        console.error('Error fetching filtered documents:', err);
      } finally {
              }
    }
    // Only fetch if filters are applied (not initial mount)
    if (chipFilters && (chipFilters.isImportant || chipFilters.isPrivate || chipFilters.availableOffline || chipFilters.category !== null)) {
      fetchFilteredDocuments();
    } else {
      // If no filters, show all documents
      setDocuments(allDocuments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipFilters, allDocuments]);

  // Use memberStats.healthSummaryReady from family stats (consistent with Family tab)
  const healthSummaryUnlocked = Boolean(memberStats?.healthSummaryReady);

  
  if (pageLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(255,255,255,0.7)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <MLoader size={40} />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 24px', paddingBottom: '160px' }}>
      <div className={styles.headerHelpIcon} data-wizard-help-icon>
        <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
      </div>
      
      <MHeader
        headerLabel="Health records"
        showProfilePickerRow
        onAdd={() => setShowUploadOptions(true)}
        module="documents"
        memberInfo={memberInfo}
        showSearch={true}
        searchPlaceholder="Try: Blood pressure, Dr Jain, Crocin..."
        showGradient={true}
        onSearch={val => {
          setSearchTerm(val);
          if (debounceTimer) clearTimeout(debounceTimer);
          const timer = setTimeout(() => {
            if (!val) {
              setDocuments(originalDocuments.length ? originalDocuments : allDocuments);
              return;
            }
            if (val.length >= 3) {
              navigate(`/documents/list?search=${encodeURIComponent(val)}`);
            }
          }, 400);
          setDebounceTimer(timer);
        }}
      />
      {/* Main UI (search, quick access, chips) always visible if user has uploaded at least one document */}
      <>
        {/* Search Documents UI */}
        <MSpacer mt={24} />

        {/* Processing Documents Pill (if any pending/processed) */}
        {processingCounts.total > 0 && (
          <div className={styles.uncategorizedPillWrapper}>
            <MPill
              icon={
                processingCounts.pending > 0 ? (
                  <IconLoader className={styles.processingSpinner} />
                ) : (
                  <IconSquareRoundedCheckFilled className={styles.processingSuccessIcon} />
                )
              }
              className={processingCounts.pending > 0 ? styles.uncategorizedPill : styles.processingSuccessPill}
              variant="banner"
              bannerText={
                <span className={processingCounts.pending > 0 ? '' : styles.processingSuccessLink}>
                  See Details
                </span>
              }
              onClick={() => navigate('/documents/upload-progress')}
              uncategorized={true}
            >
              <span className={processingCounts.pending > 0 ? styles.uncategorizedText : styles.processingSuccessText}>
                {processingCounts.pending > 0 && currentUploadingIndex > 0
                  ? `Processing ${currentUploadingIndex} of ${totalProcessingFiles} files`
                  : 'All files processed'}
              </span>
            </MPill>
          </div>
        )}

        <MSpacer mt={16} />

        {/* Uncategorized Documents Pill (24px below header) - Always shows logged-in member's uncategorized */}
        {loggedInUncategorized > 0 && (
          <div className={styles.uncategorizedPillWrapper}>
            <MPill
              icon={<IconAlertTriangle className={styles.uncategorizedIcon} />}
              className={styles.uncategorizedPill}
              variant="banner"
              bannerText={
                <span>
                  See details
                </span>
              }
              onClick={() => navigate('/documents/uncategorized')}
              uncategorized={true}
            >
              <span className={styles.uncategorizedText}>
                {pillCounts.uncategorized} file{pillCounts.uncategorized > 1 ? 's' : ''} needs your review
              </span>
            </MPill>
          </div>
        )}

        <MSpacer mt={24} />

        <div className={styles.uploadHistoryHeader}>
          <div className={styles.uploadHistoryLeft}>
  <MButton
    data-upload-history
    variant="link"
    size="md"
    style={{ padding: 0, color: '#8B3166', fontWeight: 600, display: 'flex', alignItems: 'center' }}
    onClick={() => navigate('/documents/log-listing')}
  >
    <IconHistory size={20} className={styles.uploadHistoryIcon} style={{ color: '#8B3166' }}/>
    <span className="body-s-semibold" style={{color: '#B23F83' }}>View upload history</span>
  </MButton>
</div>
            <MButton
              data-shortcuts-button
              size="md"
              variant="link"
              style={{ padding: 0, display: 'flex', alignItems: 'center' }}
              onClick={() => {
                localStorage.removeItem('documentFilters');
                navigate('/documents/quick-access');
              }}
            >
              <IconPinned size={20} style={{ color: '#45168C' }} />
              <span className="body-s-semibold" style={{ color: '#45168C', marginLeft: -3 }}>Your shortcuts</span>
            </MButton> 
          </div>
          {/* Summary cards for documents and health memories */}
          <div className="flex flex-col gap-4">
            <MButtonCard
              type="count"
              size="default"
              title={isSelf ? 'Your documents' : `${memberDisplayName}'s documents`}
              count={totalCount}
              countVariant="badge"
              icon={<IconFolders />}
              onClick={() => {
                localStorage.removeItem('documentFilters');
                navigate('/documents/navigation');
              }}
            />
            <MButtonCard
             data-health-memories-card
              type="count"
              size="default"
              title={isSelf ? 'Your health memories' : `${memberDisplayName}'s health memories`}
              count={pillCounts.healthMemories || 0}
              countVariant="badge"
              icon={<IconBubbleText />}
              onClick={() => navigate('/documents/health-memory')}
            />
          </div>

          {/* Quick actions: plain small cards */}
          <MSpacer mt={16} />
          <div className="flex flex-row gap-4">
            <div style={{ flex: 1 }}>
              <MButtonCard
                data-health-summary-card
                type="plain"
                size="sm"
                title="Health summary"
                countVariant="badge"
                icon={<IconClipboardText />}
                greyVariant={!healthSummaryUnlocked}
                onClick={() => {
                  if (!healthSummaryUnlocked) {
                    setShowHealthSummaryInfo(true);
                    return;
                  }
                  localStorage.removeItem('documentFilters');
                  setNavigationSource('overview', '/documents');
                  navigate('/health-summary' , {
                    state: { backUrl: '/documents' },
                  });
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <MButtonCard
                data-emergency-card
                type="plain"
                size="sm"
                title="Emergency card"
                countVariant="badge"
                icon={<IconMedicalCross />}
                greyVariant={memberInfo.emergencyCardReady === false}
                onClick={
                  memberInfo.emergencyCardReady === false
                    ? () => setShowEmergencyCardInfo(true)
                    : () =>
                        navigate('/dashboard/emergency-card', {
                          state: { backUrl: '/documents' },
                        })
                }
              />
            </div>
          </div>
      </>
      <MFab />
      {drawerMode === 'document' && selectedDoc && (
        <DocumentDrawer
          open={true}
          onClose={() => setDrawerMode('none')}
          onUpdate={fetchAllDocuments}
          onDocumentChange={(updatedDoc) => {
            setDocuments(prev => prev.map(doc => 
              (doc._id || doc.id) === (updatedDoc._id || updatedDoc.id) ? { ...doc, ...updatedDoc } : doc
            ));
            setSelectedDoc(prev => ({ ...prev, ...updatedDoc }));
          }}
          displayName={selectedDoc.displayName || 'Document'}
          fileKey={selectedDoc.media?.fileKey}
          onDownloadClick={async () => {
            if (!selectedDoc.media?.fileKey) return;
            await downloadDocument(selectedDoc.media.fileKey, selectedDoc.media.fileName || 'Document');
          }}
          onDelete={async () => {
            await deleteDocument(selectedDoc, setDocuments);
            setDrawerMode('none');
            window.location.reload();
          }}
          onCommentClick={() => setDrawerMode('comment')}
          commentButtonText={selectedDoc.notes ? 'Edit Comment' : 'Add Comment'}
          category={selectedDoc.category}
          document={selectedDoc}
        />
      )}
      {drawerMode === 'comment' && selectedDoc && (
        <DocumentCommentDrawer
          open={true}
          onClose={() => setDrawerMode('none')}
          initialValue={selectedDoc.notes || ''}
          loading={commentLoading}
          onDone={async (notes) => {
            setCommentLoading(true);
            try {
              await updateDocumentNotes(selectedDoc, notes, setDocuments);
              setSelectedDoc(doc => ({ ...doc, notes }));
              setDrawerMode('none');
            } finally {
              setCommentLoading(false);
            }
          }}
          title={`${selectedDoc.notes ? 'Edit Comment' : 'Add Comment'}`}
        />
      )}
      <MDocUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onDone={async () => {
          setUploadModalOpen(false);
          // Refresh document list after upload
          await fetchAllDocuments();
        }}
      />
      {/* Document Upload Options */}
      <MDocumentUploadOptions
        ref={uploadOptionsRef}
        open={showUploadOptions}
        onClose={() => setShowUploadOptions(false)}
      />
      {/* Emergency Card Dialog */}
      <MDialog
        isOpen={showEmergencyCardInfo}
        onClose={() => setShowEmergencyCardInfo(false)}
        title="Emergency card"
        description="Emergency cards help you quickly access critical information when needed.

Your card will be unlocked once you add emergency contacts, blood group, vitals, allergies & medications in your health profile."
        primaryCtaText="Unlock now"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleUnlockEmergencyCard}
        onSecondaryCta={() => setShowEmergencyCardInfo(false)}
      />

      {/* Health Summary Dialog */}
      <MDialog
        isOpen={showHealthSummaryInfo}
        onClose={() => setShowHealthSummaryInfo(false)}
        title="Health summary"
        description="Add 5 health documents, including 2 prescriptions, to unlock your Health Summary."
        primaryCtaText="Add documents"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleAddDocumentsFromHealthSummary}
        onSecondaryCta={() => setShowHealthSummaryInfo(false)}
      />
      
      <MTourTrigger ref={tourTriggerRef} page="documents" />
    </div>
  );
};
 
export default DocumentsLanding;